import type { SceneComposition, SceneObject } from '../../../types/engine.types';
import type { SceneDiffManager } from '../scene-diff-manager.class';
import type { SceneDiff } from '../types';
import { collectObjectsWithIds, getWorldPosition, hasPositionChanged } from '../helpers';

export interface ComputeDiffParams {
	newComposition: SceneComposition;
}

/**
 * Compute diff between current snapshot and new composition
 */
export const computeDiffCfn = (self: SceneDiffManager, params: ComputeDiffParams): SceneDiff => {
	const { newComposition } = params;
	const currentSnapshot = self.internalCurrentSnapshot;
	const positionTolerance = self.internalConfig.positionTolerance!;

	const diff: SceneDiff = {
		added: [],
		removed: [],
		moved: [],
		unchanged: [],
		replaced: []
	};

	// Create a map of new objects by ID
	const newObjectsById = new Map<string, SceneObject>();
	const processedIds = new Set<string>();

	// Flatten new composition objects (including nested children)
	for (const sceneObject of newComposition.objects) {
		collectObjectsWithIds(sceneObject.object, sceneObject.metadata, newObjectsById);
	}

	// Log structural vs non-structural breakdown
	const structuralTypes = ['island', 'panel', 'countertop', 'floor', 'trim', 'surround', 'corner'];
	const snapshotStructural = Array.from(currentSnapshot.values()).filter((s) =>
		structuralTypes.includes(s.type)
	);
	const newStructural = Array.from(newObjectsById.values()).filter((s) =>
		structuralTypes.includes(s.metadata.type)
	);

	// Check for ID mismatches in structural elements
	const snapshotStructuralIds = new Set(snapshotStructural.map((s) => s.id));
	const newStructuralIds = new Set(newStructural.map((s) => s.metadata.id));
	const missingInNew = Array.from(snapshotStructuralIds).filter((id) => !newStructuralIds.has(id));
	const extraInNew = Array.from(newStructuralIds).filter((id) => !snapshotStructuralIds.has(id));

	if (missingInNew.length > 0 || extraInNew.length > 0) {
		console.warn('SceneDiffManager: Structural ID mismatches detected!', {
			missingInNew: missingInNew.slice(0, 5),
			extraInNew: extraInNew.slice(0, 5)
		});
	}

	// Find removed and moved objects
	for (const [id, oldSnapshot] of Array.from(currentSnapshot.entries())) {
		const newSceneObject = newObjectsById.get(id);

		if (!newSceneObject) {
			// Object was removed
			diff.removed.push(oldSnapshot);
		} else {
			processedIds.add(id);

			// Check if geometry hash changed (object resized/reshaped but kept same ID)
			const oldHash = oldSnapshot.hash || '';
			const newHash = (newSceneObject.metadata.hash as string) || '';

			if (oldHash && newHash && oldHash !== newHash) {
				// Geometry changed — treat as replacement (remove old + add new)
				diff.replaced.push({ old: oldSnapshot, new: newSceneObject });
			} else {
				const newPos = getWorldPosition(newSceneObject.object);

				if (hasPositionChanged(oldSnapshot.position, newPos, positionTolerance)) {
					// Object moved
					diff.moved.push({
						object: oldSnapshot.object,
						from: oldSnapshot.position,
						to: newPos,
						metadata: newSceneObject.metadata
					});
				} else {
					// Object unchanged
					diff.unchanged.push(oldSnapshot);
				}
			}
		}
	}

	// Find added objects
	for (const [id, sceneObject] of Array.from(newObjectsById.entries())) {
		if (!processedIds.has(id) && !currentSnapshot.has(id)) {
			diff.added.push(sceneObject);
		}
	}

	return diff;
};
